<template>
  <section class="tw:flex tw:max-w-[760px] tw:flex-col tw:gap-7" data-testid="reg-step-company" @focusout="onFocusOut">
    <RegistrationStepHeading :title="$t('registration.company.title')" :lede="$t('registration.company.lede')" />

    <CompanyBasicsSection
      v-model:draft="draft"
      :errors="shownErrors"
      :name-confirmed="nameConfirmed"
      @name-blur="onCompanyNameBlur" />

    <div class="tw:h-px tw:bg-gray-200" />

    <CompanyAddressSection
      ref="addressSection"
      v-model:draft="draft"
      v-model:uid-is-valid="uidIsValid"
      v-model:name-confirmed="nameConfirmed"
      :errors="shownErrors"
      :work-email="workEmail" />

    <div class="tw:h-px tw:bg-gray-200" />

    <CompanyBrandSection v-model:draft="draft" :existing-logo-url="hydratedLogoUrl" />
  </section>
</template>

<script setup lang="ts">
/** Step 4 - company details; the country is omitted because the backend stores Switzerland automatically */
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';

import { registrationClient } from '@/api/registration';
import CompanyAddressSection from '@/components/registration/subcomponents/CompanyAddressSection.vue';
import CompanyBasicsSection from '@/components/registration/subcomponents/CompanyBasicsSection.vue';
import CompanyBrandSection from '@/components/registration/subcomponents/CompanyBrandSection.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import { useRegistrationStepSync } from '@/composables/useRegistrationStepSync';
import {
  readRegistrationErrorBody,
  registrationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import { isValidUid, normalizeUid } from '@/utils/uid';

interface CompanyFieldErrors {
  name: string;
  careersPage: string;
  industry: string;
  companySize: string;
  street: string;
  postcode: string;
  city: string;
  invoiceEmail: string;
  billingStreet: string;
  billingPostcode: string;
  billingCity: string;
  uid: string;
}

/** maps backend snake_case fields to the camelCase form error keys; backend messages are already localized */
const FIELD_ERROR_KEYS: Partial<Record<string, keyof CompanyFieldErrors>> = {
  name: 'name',
  careers_page: 'careersPage',
  industry: 'industry',
  company_size: 'companySize',
  street: 'street',
  postcode: 'postcode',
  city: 'city',
  invoice_email: 'invoiceEmail',
  billing_street: 'billingStreet',
  billing_postcode: 'billingPostcode',
  billing_city: 'billingCity',
  uid: 'uid',
};

/** maps a field's test id to its error key, so a focusout can mark it as touched */
const TESTID_FIELDS: Record<string, keyof CompanyFieldErrors> = {
  'reg-company-name': 'name',
  'reg-company-industry': 'industry',
  'reg-company-size': 'companySize',
  'reg-company-uid': 'uid',
  'reg-company-street': 'street',
  'reg-company-postcode': 'postcode',
  'reg-company-city': 'city',
  'reg-company-billing-street': 'billingStreet',
  'reg-company-billing-postcode': 'billingPostcode',
  'reg-company-billing-city': 'billingCity',
};

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();
const toast = useToast();

/** thumbnail of an already-uploaded logo; presentation only, never written to session storage */
const hydratedLogoUrl = ref<string | null>(null);

/** UID check result from CompanyAddressSection; true on a register outage so it never blocks the registrant */
const uidIsValid = ref(false);

/** lets the name field's blur ask the address section to re-check */
const addressSection = ref<InstanceType<typeof CompanyAddressSection> | null>(null);

/** whether the shown name is the one the register confirmed; owned by the address section's UID check */
const nameConfirmed = ref(false);

const fieldErrors = reactive<CompanyFieldErrors>({
  name: '',
  careersPage: '',
  industry: '',
  companySize: '',
  street: '',
  postcode: '',
  city: '',
  invoiceEmail: '',
  billingStreet: '',
  billingPostcode: '',
  billingCity: '',
  uid: '',
});

/** fields the registrant has left at least once, so required errors don't show while still typing */
const touched = reactive(new Set<keyof CompanyFieldErrors>());

/* --------------------------------- COMPUTED --------------------------------- */
const draft = computed(() => store.draft.company);

const workEmail = computed(() => store.draft.account.email);

const isFormValid = computed(
  () =>
    draft.value.name.trim() !== '' &&
    draft.value.street.trim() !== '' &&
    draft.value.postcode.trim() !== '' &&
    draft.value.city.trim() !== '' &&
    draft.value.industry !== '' &&
    draft.value.companySize !== '' &&
    uidIsValid.value &&
    (draft.value.billingSameAsCompany ||
      (draft.value.billingStreet.trim() !== '' &&
        draft.value.billingPostcode.trim() !== '' &&
        draft.value.billingCity.trim() !== ''))
);

/** backend errors, plus required and malformed-UID messages for fields the registrant has left */
const shownErrors = computed<CompanyFieldErrors>(() => {
  const d = draft.value;
  const empty: Partial<Record<keyof CompanyFieldErrors, boolean>> = {
    name: d.name.trim() === '',
    industry: d.industry === '',
    companySize: d.companySize === '',
    uid: d.uid.trim() === '',
    street: d.street.trim() === '',
    postcode: d.postcode.trim() === '',
    city: d.city.trim() === '',
    billingStreet: !d.billingSameAsCompany && d.billingStreet.trim() === '',
    billingPostcode: !d.billingSameAsCompany && d.billingPostcode.trim() === '',
    billingCity: !d.billingSameAsCompany && d.billingCity.trim() === '',
  };
  const shown = { ...fieldErrors };

  for (const key of touched) {
    if (!shown[key] && empty[key]) shown[key] = translator.get('registration.company.field_required');
  }

  // a half-typed UID (e.g. without the CHE prefix) is only judged once the field is left
  const uid = normalizeUid(d.uid);
  if (touched.has('uid') && !shown.uid && uid !== '' && !isValidUid(uid)) {
    shown.uid = translator.get('registration.company.uid_invalid');
  }

  return shown;
});

/* --------------------------------- METHODS ---------------------------------- */
function onFocusOut(event: FocusEvent) {
  const testId = (event.target as HTMLElement | null)?.closest('[data-testid]')?.getAttribute('data-testid');
  const key = testId ? TESTID_FIELDS[testId] : undefined;
  if (key) touched.add(key);
}

/** editing the name can turn a UID match into a mismatch, so re-check on blur; no-op without a locally-valid UID */
function onCompanyNameBlur() {
  addressSection.value?.recheckWithCurrentName();
}

function clearErrors() {
  fieldErrors.name = '';
  fieldErrors.careersPage = '';
  fieldErrors.industry = '';
  fieldErrors.companySize = '';
  fieldErrors.street = '';
  fieldErrors.postcode = '';
  fieldErrors.city = '';
  fieldErrors.invoiceEmail = '';
  fieldErrors.billingStreet = '';
  fieldErrors.billingPostcode = '';
  fieldErrors.billingCity = '';
  fieldErrors.uid = '';
}

async function submit(): Promise<boolean> {
  clearErrors();

  try {
    await registrationClient.submitCompany({
      name: draft.value.name,
      careersPage: draft.value.careersPage,
      industry: draft.value.industry,
      companySize: draft.value.companySize,
      street: draft.value.street,
      postcode: draft.value.postcode,
      city: draft.value.city,
      uid: draft.value.uid,
      invoiceEmail: draft.value.invoiceEmail,
      // 'same as company' is frontend-only; the backend always gets an explicit billing address
      billingStreet: draft.value.billingSameAsCompany ? draft.value.street : draft.value.billingStreet,
      billingPostcode: draft.value.billingSameAsCompany ? draft.value.postcode : draft.value.billingPostcode,
      billingCity: draft.value.billingSameAsCompany ? draft.value.city : draft.value.billingCity,
      logoId: draft.value.logoId,
      brandColors: draft.value.brandColors.filter((color) => color.value),
    });

    return true;
  } catch (error) {
    const backendErrors = readRegistrationErrorBody(error)?.errors;

    // no recognisable field error and the shared client's toast is off for this call
    if (!backendErrors || Object.keys(backendErrors).length === 0) {
      toast.error(registrationErrorMessage(error, translator.get('common.network_error')));
      return false;
    }

    for (const [field, messages] of Object.entries(backendErrors)) {
      const key = FIELD_ERROR_KEYS[field];
      const message = messages[0];
      if (key && message) fieldErrors[key] = message;
    }

    return false;
  }
}

/** pre-fills the draft from the backend, then sets the logo thumbnail and re-checks the UID (children's mount-time check ran before hydration) */
useRegistrationStepSync('company', (state, patchedFields) => {
  hydratedLogoUrl.value = state.submitted.company.logoUrl;
  allowNext.value = isFormValid.value;

  if (patchedFields.includes('uid')) addressSection.value?.recheckWithCurrentName();
});

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.company.onContinue = submit;
  allowNext.value = isFormValid.value;
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(isFormValid, (valid) => {
  allowNext.value = valid;
});
</script>
